import { useState, useRef, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import SocialAuthButtons from '@/components/SocialAuthButtons';
import {
  isBiometricAvailable,
  isBiometricLoginEnabled,
  getBiometricLabel,
  biometricSignIn,
  storeAuthToken,
  storeLastEmail,
} from '@/lib/biometricAuth';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';
import { RADIUS, TRACKING } from '@/constants/designTokens';
import { DUEL_LOGO } from '../../assets/images';

const log = createLogger('SIGNIN');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignInScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { signIn } = useAuth();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasNavigatedRef = useRef(false);
  const passwordRef = useRef<TextInput>(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState('Biometric Login');

  useEffect(() => {
    (async () => {
      const available = await isBiometricAvailable();
      const enabled = await isBiometricLoginEnabled();
      if (available && enabled) {
        setBiometricAvailable(true);
        setBiometricLabel(await getBiometricLabel());
      }
    })();
  }, []);

  const handleBiometricSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await biometricSignIn();
      if (result.success && result.token && result.refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.token,
          refresh_token: result.refreshToken,
        });
        if (sessionError) {
          setError('Biometric session expired. Please sign in with your password.');
        } else if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          const returnTo = typeof params.returnTo === 'string' ? params.returnTo : null;
          const isSafeReturn =
            returnTo !== null &&
            (returnTo.startsWith('/org/invite/') || returnTo.startsWith('/org-roster'));
          if (isSafeReturn) {
            router.replace(returnTo as any);
          } else {
            router.replace('/gate');
          }
        }
      } else {
        setError('Biometric authentication failed.');
      }
    } catch (e) {
      console.warn('Biometric authentication error:', e);
      setError('Biometric authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Please fill in all fields.');
      return;
    }

    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await signIn(trimmedEmail, password);

      log.info('[SIGNIN] Result:', result);

      if (!result.ok) {
        log.info('[SIGNIN] Error:', result.errorMessage);
        setError(result.errorMessage || 'Failed to sign in');
        return;
      }

      // Store token for biometric re-auth
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.access_token) {
          await storeAuthToken(sessionData.session.access_token, sessionData.session.refresh_token);
          await storeLastEmail(trimmedEmail);
        }
      } catch (e) { console.warn('Non-critical auth token storage failed:', e); }

      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        // If a returnTo param is present and is a safe route, navigate there after login
        const returnTo = typeof params.returnTo === 'string' ? params.returnTo : null;
        const isSafeReturn =
          returnTo !== null &&
          (returnTo.startsWith('/org/invite/') || returnTo.startsWith('/org-roster'));
        if (isSafeReturn) {
          router.replace(returnTo as any);
        } else {
          router.replace('/gate');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <KeyboardAvoidingView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
      <View style={styles.header}>
        <View style={{ width: 40 }} />
      </View>

      <View style={styles.content}>
        <Image source={DUEL_LOGO} style={styles.logo} resizeMode="contain" accessibilityLabel="DUEL logo" />
        <Text style={[styles.title, { color: theme.colors.text }]} accessibilityRole="header">Sign In</Text>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>Access your account</Text>

        {error ? <Text style={[styles.errorText, { color: theme.colors.accent, backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>{error}</Text> : null}

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Email</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
              value={email}
              onChangeText={(text) => { setEmail(text); setError(''); }}
              placeholder="your@email.com"
              placeholderTextColor={theme.colors.textTertiary}
              autoCapitalize="none"
              autoComplete="email"
              keyboardType="email-address"
              returnKeyType="next"
              onSubmitEditing={() => passwordRef.current?.focus()}
              editable={!loading}
              accessibilityLabel="Email address"
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Password</Text>
            <TextInput
              ref={passwordRef}
              style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
              value={password}
              onChangeText={(text) => { setPassword(text); setError(''); }}
              placeholder="••••••••"
              placeholderTextColor={theme.colors.textTertiary}
              secureTextEntry
              autoComplete="password"
              returnKeyType="go"
              onSubmitEditing={handleSignIn}
              editable={!loading}
              accessibilityLabel="Password"
            />
          </View>

          <Pressable
            style={[styles.button, { backgroundColor: theme.colors.accent }, loading && styles.buttonDisabled]}
            onPress={handleSignIn}
            disabled={loading}
            accessibilityRole="button"
            accessibilityLabel="Sign In"
            android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
            hitSlop={{ top: 8, bottom: 8 }}>
            {loading ? (
              <ActivityIndicator color={theme.colors.accentForeground} />
            ) : (
              <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Sign In</Text>
            )}
          </Pressable>

          {biometricAvailable && (
            <TouchableOpacity
              style={[styles.biometricButton, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
              onPress={handleBiometricSignIn}
              disabled={loading}
              accessibilityRole="button"
              accessibilityLabel={`Sign in with ${biometricLabel}`}>
              <Text style={[styles.biometricButtonText, { color: theme.colors.text }]}>
                Use {biometricLabel}
              </Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            onPress={() => router.push('/(auth)/forgot-password')}
            disabled={loading}
            style={styles.forgotPasswordButton}>
            <Text style={[styles.forgotPasswordText, { color: theme.colors.textTertiary }]}>Forgot your password?</Text>
          </TouchableOpacity>

          <SocialAuthButtons />

          <TouchableOpacity
            onPress={() => {
              const gymParam = params.gym ? `?gym=${params.gym}` : '';
              const returnToParam = params.returnTo ? `${gymParam ? '&' : '?'}returnTo=${encodeURIComponent(params.returnTo as string)}` : '';
              router.push(`/(auth)/signup${gymParam}${returnToParam}` as any);
            }}
            disabled={loading}>
            <Text style={[styles.linkText, { color: theme.colors.textSecondary }]}>
              Don&apos;t have an account?{' '}
              <Text style={[styles.linkTextBold, { color: theme.colors.accent }]}>Sign Up</Text>
            </Text>
          </TouchableOpacity>

        </View>
      </View>
    </ScrollView>
    </KeyboardAvoidingView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    paddingHorizontal: 32,
    paddingTop: 80,
  },
  logo: {
    width: 180,
    height: 180,
    alignSelf: 'center',
    marginBottom: 48,
  },
  title: {
    fontSize: 36,
    fontFamily: TYPO.heading.bold,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.normal,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: TYPO.subheading.bold,
    marginBottom: 48,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wider,
  },
  form: {
    gap: 32,
  },
  inputContainer: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wider,
  },
  input: {
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    paddingHorizontal: 16,
    paddingVertical: 16,
    fontSize: 16,
  },
  button: {
    paddingVertical: 18,
    borderRadius: RADIUS.none,
    alignItems: 'center',
    marginTop: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
  forgotPasswordButton: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  forgotPasswordText: {
    fontSize: 14,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.normal,
    textDecorationLine: 'underline',
  },
  linkText: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.normal,
  },
  linkTextBold: {
    fontWeight: '700',
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  biometricButton: {
    paddingVertical: 16,
    borderRadius: RADIUS.none,
    alignItems: 'center',
    borderWidth: 1,
  },
  biometricButtonText: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
});
